// src/components/auth/Login.jsx
import { useState } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from "@/components/ui/use-toast";

const Login = () => {
  const { login, db, user } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      // First, wait for login to complete
      const userCredential = await login(email, password);
      
      // Wait for auth state to be ready and get the user
      if (!userCredential.user) {
        throw new Error('No user data available after login');
      }

      // Now safely access user.uid
      const pilotDoc = await getDoc(doc(db, 'pilot', userCredential.user.uid));
      
      if (!pilotDoc.exists() || !pilotDoc.data().onboarding) {
        await updateDoc(doc(db, 'pilot', userCredential.user.uid), {
          onboarding: {
            isComplete: false,
            certificates: false,
            medical: false,
            aircraft: false,
            experience: false,
            steps: [],
            lastUpdated: new Date().toISOString(),
            dateStarted: new Date().toISOString()
          }
        });
      }

      const from = location.state?.from?.pathname || '/chat';
      navigate(from, { replace: true });
      
      toast({
        title: "Welcome back!",
        description: "You've successfully logged in.",
      });
    } catch (error) {
      console.error('Login error:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || 'Failed to log in. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative">
      {/* Nav header */}
      <div className="nav-header mx-auto w-full">
              <Link to="/" className="flex items-center gap-2">
                <div className="bg-aviation-advisor-nav-logo h-12 w-12" />
                <span className="g100 font-urbanist h600 text-gray-100">Aviation Advisor</span>
              </Link>
      </div>
      
      <div className="form-group items-center py-6">
        {/* Title */}
        <div className="flex justify-center">
          <h1 className="h700">Sign into Account</h1>
        </div>
        
        {/* Subtitle */}
        <div className="flex justify-center">
          <p className="p-m text-gray-100 w-80 align-center">
            Welcome to Aviation Advisor AI, your personal aviation advisor for flight planning, weather, and more. Understand NOTAMs in plain English!
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col items-center gap-4">
          {/* Email Field */}
          <div className="input-container">
            <div className="input-label-row">
              <label className="input-label  pb-0 mb-3">Email</label>
            </div>
            <input
              id="email"
              name="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-field"
              placeholder="Email address"
            />
          </div>

          {/* Password Field */}
          <div className="input-container mb-6">
            <div className="input-label-row">
              <label className="input-label pb-0 mb-3">Password</label>
            </div>
            <div className="password-container">
              <input
                id="password"
                name="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="password-field"
                placeholder="Password"
              />
            </div>
            
            {/* Forgot Password Link */}
            <Link to="/forgot-password" className="text-blue-60 action-s">
              Forgot your password?
            </Link>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="submit-button"
          >
            {loading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>

        {/* Divider */}
        <div className="flex flex-col items-center w-[22.875rem] gap-6">
          <img
            className="h-px w-full mt-6"
            src="/assets/Line.svg"
            loading="lazy"
            alt="Login illustration"
          />

        {/* Sign Up Link */}
          <div className="flex justify-center gap-1 w-stretch">
          <span className="flex p-m w-stretch justify-end">
            Don't have an account?
          </span>
          <Link to="/signup" className="text-blue-60 action-m w-stretch flex-1 justify-start">
            Sign up
          </Link>
        </div>
      </div>
    </div>
  </div>
  );
};

export default Login;